import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import SEOHead from "@/components/SEOHead";
import { Loader2, Sparkles, Trash2, Eye, EyeOff, Edit2, ExternalLink, ArrowLeft } from "lucide-react";
import BlogPostEditor from "@/components/BlogPostEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function BlogAdminPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/blog/posts`),
        axios.get(`${API}/admin/blog/scheduler-status`),
      ]);
      setPosts(pRes.data);
      setStatus(sRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load admin blog");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // Gate: only admin role can see this page (after hooks have run)
  if (user && user.role !== "admin") {
    return <Navigate to="/app" replace />;
  }

  const generateNow = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/blog/generate-topics`, { count: 2 });
      toast.success(`Generated ${data.count} draft${data.count === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const togglePublish = async (post) => {
    const next = post.status === "published" ? "draft" : "published";
    try {
      await axios.patch(`${API}/admin/blog/posts/${post.id}`, { status: next });
      toast.success(next === "published" ? "Published" : "Unpublished");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update");
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}" permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${post.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400">Loading…</p>
      </div>
    );
  }

  const drafts = posts.filter((p) => p.status === "draft");
  const published = posts.filter((p) => p.status === "published");

  return (
    <div className="min-h-screen bg-white">
      <SEOHead title="Blog admin — BreakItDown" path="/admin/blog" noindex />
      <header className="border-b border-zinc-200 sticky top-0 z-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/app" className="text-zinc-500 hover:text-zinc-900 flex items-center gap-1.5" data-testid="admin-back-to-app">
              <ArrowLeft className="w-4 h-4" /> <span className="text-sm">App</span>
            </Link>
            <h1 className="text-base font-semibold tracking-tight">Blog admin</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/blog" target="_blank" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
              View live blog <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        {/* Stats + generate */}
        <section className="grid sm:grid-cols-4 gap-3 mb-8">
          <Stat label="Drafts" value={drafts.length} data-testid="admin-stat-drafts" />
          <Stat label="Published" value={published.length} data-testid="admin-stat-published" />
          <Stat
            label="Last AI run"
            value={status?.last_generated_at ? formatDate(status.last_generated_at) : "—"}
          />
          <div className="border border-zinc-900 p-4 flex flex-col justify-between">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">Weekly cadence</p>
            <button
              onClick={generateNow}
              disabled={generating}
              className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-900 hover:bg-[#002FA7] disabled:opacity-50 text-white text-xs font-medium uppercase tracking-[0.12em] transition-colors"
              data-testid="admin-generate-now-btn"
            >
              {generating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              {generating ? "Generating…" : "Generate now"}
            </button>
          </div>
        </section>

        {loading ? (
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 text-center py-12">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-12 text-center">
            <p className="text-sm text-zinc-500">
              No blog posts yet. Click <strong>Generate now</strong> to create your first two drafts.
            </p>
          </div>
        ) : (
          <>
            <PostSection
              title={`Drafts (${drafts.length})`}
              posts={drafts}
              onTogglePublish={togglePublish}
              onDelete={deletePost}
              onEdit={(p) => setEditingId(p.id)}
              isDraftSection
            />
            <PostSection
              title={`Published (${published.length})`}
              posts={published}
              onTogglePublish={togglePublish}
              onDelete={deletePost}
              onEdit={(p) => setEditingId(p.id)}
            />
          </>
        )}
      </main>

      {editingId && (
        <BlogPostEditor
          postId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            setEditingId(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, ...rest }) {
  return (
    <div className="border border-zinc-200 p-4" {...rest}>
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">{label}</p>
      <p className="text-2xl font-semibold text-zinc-900 mt-2 truncate">{value}</p>
    </div>
  );
}

function PostSection({ title, posts, onTogglePublish, onDelete, onEdit, isDraftSection }) {
  if (posts.length === 0) return null;
  return (
    <section className="mb-10">
      <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-3">{title}</h2>
      <div className="border border-zinc-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left p-3 text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Title</th>
              <th className="hidden md:table-cell text-left p-3 text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Category</th>
              <th className="hidden md:table-cell text-left p-3 text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">
                {isDraftSection ? "Created" : "Published"}
              </th>
              <th className="text-right p-3 text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-b border-zinc-200 last:border-0" data-testid={`admin-post-${p.slug}`}>
                <td className="p-3">
                  <div className="font-medium text-zinc-900 line-clamp-1">{p.title}</div>
                  <div className="text-xs text-zinc-500 font-mono">{p.slug}</div>
                </td>
                <td className="hidden md:table-cell p-3 text-zinc-600">{p.category || "—"}</td>
                <td className="hidden md:table-cell p-3 text-zinc-600 text-xs">
                  {formatDate(isDraftSection ? p.created_at : p.published_at)}
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onEdit(p)}
                      className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                      title="Edit"
                      data-testid={`admin-edit-${p.slug}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onTogglePublish(p)}
                      className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                      title={p.status === "published" ? "Unpublish" : "Publish"}
                      data-testid={`admin-toggle-${p.slug}`}
                    >
                      {p.status === "published" ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => onDelete(p)}
                      className="p-1.5 text-zinc-500 hover:text-red-700 hover:bg-red-50 transition-colors"
                      title="Delete"
                      data-testid={`admin-delete-${p.slug}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
